import { useMemo } from 'react'
import { useData } from '../contexts/DataContext'
import { useLocation } from '../contexts/LocationContext'
import { useFilters } from '../contexts/FilterContext'

export function useFilteredFacilities() {
  const { facilities, attributes } = useData()
  const { currentLocationCode } = useLocation()
  const {
    activeCategory,
    activeFilters,
    searchTerm,
    getGroupFilters: _getGroupFilters,
  } = useFilters()

  return useMemo(() => {
    const facilityData = facilities[activeCategory] || []
    const categoryAttributes = attributes[activeCategory]

    if (!facilityData.length) {
      return {
        facilities: [],
        total: 0,
        filtered: 0,
      }
    }

    let filtered = [...facilityData]

    // Apply location filters using location codes (hierarchical)
    if (currentLocationCode) {
      filtered = filtered.filter(facility => {
        if (!facility.location_code) return false
        // Check if facility's location code starts with the selected location code
        return facility.location_code.startsWith(currentLocationCode)
      })
    }

    // Apply attribute filters with AND operation for all selections
    if (categoryAttributes && Object.keys(activeFilters).length > 0) {
      filtered = filtered.filter(facility => {
        // All active filters must match (AND operation across all filters)
        const allFilters = []
        Object.entries(activeFilters).forEach(([, groupFilters]) => {
          Object.entries(groupFilters).forEach(([filterId, filterValue]) => {
            allFilters.push({ filterId, filterValue })
          })
        })

        return allFilters.every(({ filterId, filterValue }) => {
          // Find the attribute definition
          const attribute = findAttributeById(categoryAttributes, filterId)
          if (!attribute) return true

          const facilityValue = facility[attribute.column]

          if (attribute.type === 'boolean') {
            // For boolean filters, match exact value
            return facilityValue === filterValue
          }

          if (attribute.type === 'level' || attribute.type === 'ownership') {
            // For level/ownership filters, match exact value
            return facilityValue === filterValue
          }

          // For other types, implement as needed
          return true
        })
      })
    }

    // Apply search term filter (case-insensitive partial match on name)
    if (searchTerm && searchTerm.trim().length > 0) {
      const term = searchTerm.toLowerCase().trim()
      filtered = filtered.filter(facility => {
        // Get the appropriate name field based on category
        const nameField =
          activeCategory === 'health'
            ? facility.name
            : facility.institution_name || facility.school_name || facility.name

        return nameField && nameField.toLowerCase().includes(term)
      })
    }

    return {
      facilities: filtered,
      total: facilityData.length,
      filtered: filtered.length,
    }
  }, [
    facilities,
    attributes,
    activeCategory,
    currentLocationCode,
    activeFilters,
    searchTerm,
  ])
}

// Helper function to find attribute by ID in nested groups
function findAttributeById(categoryAttributes, attributeId) {
  if (!categoryAttributes?.groups) return null

  for (const group of categoryAttributes.groups) {
    const attribute = group.attributes.find(attr => attr.id === attributeId)
    if (attribute) return attribute
  }

  return null
}
